/**
 * List input widget — one text field per item, add/remove, bounded by
 * min_items/max_items. Mirrors the app's ListInputBuilder: the value on the
 * wire is a string[] (the ListInput node takes list[str]), never a joined
 * string, which is what the fallback text box used to post.
 */

import type { FunctionComponent } from 'preact'

interface ListInputProps {
  value: unknown
  onChange: (value: string[]) => void
  itemWidget?: string | null
  itemLabel?: string | null
  minItems?: number | null
  maxItems?: number | null
  disabled?: boolean
}

/** Coerce whatever a default or a stored value carries into string[]. */
function normalizeToStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item ?? ''))
  if (typeof value === 'string') {
    // A legacy default may be a JSON-encoded list.
    try {
      const parsed = JSON.parse(value) as unknown
      if (Array.isArray(parsed)) return parsed.map((item) => String(item ?? ''))
    } catch {
      // not JSON — fall through
    }
    return value ? [value] : []
  }
  return []
}

export const ListInput: FunctionComponent<ListInputProps> = ({
  value,
  onChange,
  itemWidget,
  itemLabel,
  minItems,
  maxItems,
  disabled,
}) => {
  const items = normalizeToStringArray(value)
  const min = minItems ?? 0
  // Always show at least the required number of rows so the user sees what is
  // expected instead of an empty widget with a single "Tilføj" button.
  const rows = items.length < min ? [...items, ...Array<string>(min - items.length).fill('')] : items
  const label = itemLabel || 'element'
  const canAdd = !disabled && (maxItems == null || rows.length < maxItems)
  const canRemove = !disabled && rows.length > min

  const update = (index: number, itemValue: string) => {
    const next = [...rows]
    next[index] = itemValue
    onChange(next)
  }
  const remove = (index: number) => onChange(rows.filter((_, i) => i !== index))
  const add = () => onChange([...rows, ''])

  return (
    <div class="syv-list">
      {rows.length > 0 && (
        <ul class="syv-list-items">
          {rows.map((item, index) => (
            <li class="syv-list-item" key={index}>
              {itemWidget === 'textarea' ? (
                <textarea
                  class="syv-input syv-textarea"
                  value={item}
                  placeholder={`${label} ${index + 1}`}
                  disabled={disabled}
                  rows={2}
                  onInput={(e) => update(index, (e.target as HTMLTextAreaElement).value)}
                />
              ) : (
                <input
                  type="text"
                  class="syv-input"
                  value={item}
                  placeholder={`${label} ${index + 1}`}
                  disabled={disabled}
                  onInput={(e) => update(index, (e.target as HTMLInputElement).value)}
                />
              )}
              {canRemove && (
                <button
                  type="button"
                  class="syv-icon-button"
                  aria-label={`Fjern ${label} ${index + 1}`}
                  onClick={() => remove(index)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canAdd && (
        <button type="button" class="syv-button syv-button-secondary" onClick={add}>
          Tilføj {label}
        </button>
      )}
      {(min > 0 || maxItems != null) && (
        <span class="syv-hint">
          {[
            min > 0 ? `Mindst ${min}` : null,
            maxItems != null ? `Højst ${maxItems}` : null,
            `Nuværende: ${rows.length}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )}
    </div>
  )
}
